import { Component } from '@theme/component';

/**
 * Scentreat add-on upsell box (PDP buy box).
 * - "addon" mode: a real checkbox. When checked and the customer adds the main product,
 *   the add-on product is added right after through its own hidden Horizon product form,
 *   so the cart drawer / icon update through the theme's native machinery.
 * - The main Add-to-cart button gets a price suffix (base vs combined) that follows the checkbox.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [checkbox]
 * @property {HTMLElement} [addonForm]
 */

/** @extends {Component<Refs>} */
class StUpsellComponent extends Component {
  requiredRefs = [];

  /** @type {HTMLElement | null} */
  #mainForm = null;

  /** @type {HTMLElement | null} */
  #priceEl = null;

  #onCartUpdate = (event) => {
    if (!this.refs.checkbox || !this.refs.checkbox.checked) return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.#mainForm || target !== this.#mainForm) return;
    // Already added in the same /cart/add.js request by product-form (see #collectAddonVariantIds)
    const mainFormEl = this.#mainForm.querySelector('form');
    if (mainFormEl && mainFormEl.dataset.stBatched === '1') {
      const p = event.promise || (event.detail && event.detail.promise);
      const clear = () => { delete mainFormEl.dataset.stBatched; };
      if (p && typeof p.then === 'function') p.then(clear, clear); else clear();
      return;
    }
    // Let the main add settle, then push the add-on through its own product form.
    const index = [...document.querySelectorAll('.st-upsell--addon')].indexOf(this);
    const submit = () => {
      const form = this.refs.addonForm ? this.refs.addonForm.querySelector('form') : null;
      setTimeout(() => form?.requestSubmit(), Math.max(0, index) * 700);
    };
    const promise = event.promise || (event.detail && event.detail.promise);
    if (promise && typeof promise.then === 'function') {
      promise.then(submit).catch(submit);
    } else {
      setTimeout(submit, 800);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section') || document;
    const forms = [...section.querySelectorAll('product-form-component')];
    // The main buy form is the one not owned by any upsell box (each addon box has its own hidden form).
    this.#mainForm = forms.find((f) => !f.closest('st-upsell-component')) || null;
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('CartLinesUpdateEvent', this.#onCartUpdate);
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        if (StandardEvents && StandardEvents.cartLinesUpdate) {
          document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
        }
      })
      .catch(() => {});
    this.#mountPrice();
    this.#syncPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.removeEventListener('CartLinesUpdateEvent', this.#onCartUpdate);
  }

  toggle() {
    this.#syncPrice();
  }

  #mountPrice() {
    if (!this.dataset.priceBase || !this.#mainForm) return;
    const button = this.#mainForm.querySelector('.add-to-cart-button');
    if (!button) return;
    let el = button.querySelector('.st-atc-price');
    if (!el) {
      el = document.createElement('span');
      el.className = 'st-atc-price';
      button.insertBefore(el, button.querySelector('.add-to-cart__added'));
    }
    this.#priceEl = el;
  }

  #syncPrice() {
    if (!this.#priceEl) return;
    const checked = this.refs.checkbox ? this.refs.checkbox.checked : false;
    const price = checked ? this.dataset.priceCombined : this.dataset.priceBase;
    if (price) this.#priceEl.textContent = ` - ${price} →`;
  }
}

if (!customElements.get('st-upsell-component')) {
  customElements.define('st-upsell-component', StUpsellComponent);
}
